import { useEffect, useState } from "react"
import Api from "../api/api"
import roleUtilities from "../../utilities/roleUtilities"
import stringHelpers from "../../utilities/stringHelpers"
import { Modal } from "react-bootstrap"
import { Link, useLocation } from 'react-router-dom';
import { HoverTooltip } from "../shared/hoverTooltip"
import Pagination from "../shared/pagination"
import { ErrorModal } from "../../utilities/errorModal"
import arrayUtilities from "../../utilities/arrayUtilities"
import { EmptyState } from "../dashboard/emptyState"
import Loader from "../shared/loader"
import { toDateTimeString, isDateTimeElapsed } from "../../utilities/dateUtilities"
import formUtilities from "../../utilities/formUtilities"
import { SearchBarWithDebounce } from "../shared/searchBarWithDebounce"
import { FiltersModalTrigger } from "../shared/filtersModalTrigger"
import { AppliedFilterPill, TagFilterItemLabel } from "../shared/appliedFilterPill"
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities"
import { EnforcementSuspensionsFiltersModal } from "./enforcementSuspensionsFiltersModal"

export const EnforcementSuspensions = ({ sites, tags, user, organisationId, token }) => {
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(false)
    const [suspensions, setSuspensions] = useState(null)
    const [pageNumber, setPageNumber] = useState(0);
    const [sitesByClient, setSitesByClient] = useState(sites)
    const [errorMessage, setErrorMessage] = useState(false)
    const [copyConfig, setCopyConfig] = useState(null)
    const [isCopying, setIsCopying] = useState(false)
    const [search, setSearch] = useState("");
    const [selectedSites, setSelectedSites] = useState(null)
    const [selectedTags, setSelectedTags] = useState([])
    const [filterEnd, setFilterEnd] = useState(null);
    const [filterStart, setFilterStart] = useState(null);
    const [deleteId, setDeleteId] = useState(null)
    const [showFiltersModal, setShowFiltersModal] = useState(false)
    const [showFilterPills, setShowFilterPills] = useState(false)
    const [appliedFilters, setAppliedFilters] = useState(null)
    const getSuspensions = (query, page, siteIds, from, to, tagIds) => {
        setIsLoading(true);
        Api.getSuspensions(
            data => {
                setSuspensions(data)
                setIsLoading(false);
            },
            error => {
                console.log(error)
                setSuspensions(null)
                setIsLoading(false);
            },
            stringHelpers.isNullOrEmpty(query) ? null : query, page, 100, siteIds, from, to, tagIds, organisationId, token
        )
    }
    const changePageNumber = (num) => {
        setPageNumber(num);
        getSuspensions(search, num, selectedSites, filterStart, filterEnd, selectedTags)
    };

    useEffect(() => {
        const initialSiteId = location.state?.siteId ?? null
        if (initialSiteId) {
            setSelectedSites([initialSiteId])
            getSuspensions(null, 0, [initialSiteId], null, null, selectedTags)
        } else {
            getSuspensions(null, 0, null, null, null, selectedTags)
        }
    }, [])

    const clearFilters = function () {
        setSelectedSites(null);
        setSelectedTags([]);
        setFilterStart(null);
        setFilterEnd(null);
        setAppliedFilters(null);
        setShowFilterPills(false);
        setPageNumber(0);
        getSuspensions(search, 0, null, null, null, [])
    };

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Sites":
                setSelectedSites(null)
                getSuspensions(search, 0, null, filterStart, filterEnd, selectedTags)
                break
            case "From":
                setFilterStart(null)
                getSuspensions(search, 0, selectedSites, null, filterEnd, selectedTags)
                break
            case "To":
                setFilterEnd(null)
                getSuspensions(search, 0, selectedSites, filterStart, null, selectedTags)
                break
            case "Date Range":
                setFilterStart(null)
                setFilterEnd(null)
                getSuspensions(search, 0, selectedSites, null, null, selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                getSuspensions(search, 0, selectedSites, filterStart, filterEnd, [])
                break
            default:
                break
        }
        setPageNumber(0)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Sites":
                setSelectedSites(remainingIds)
                getSuspensions(search, 0, remainingIds, filterStart, filterEnd, selectedTags)
                break
            case "Tags":
                setSelectedTags(remainingIds)
                getSuspensions(search, 0, selectedSites, filterStart, filterEnd, remainingIds)
                break
            default:
                break
        }
        setPageNumber(0)
    }

    const handleCopy = () => {
        setIsCopying(true)
        if (copyConfig) {
            Api.copySuspension(
                data => {
                    setIsCopying(false)
                    if (data.isError) {
                        setErrorMessage(data.errorMessage)
                    } else {
                        getSuspensions(search, pageNumber, selectedSites, filterStart, filterEnd, selectedTags)
                    }
                    setCopyConfig(prevState => ({
                        ...prevState,
                        showModal: false
                    }))
                },
                error => {
                    setIsCopying(false)
                    setCopyConfig(prevState => ({
                        ...prevState,
                        showModal: false
                    }))
                    setErrorMessage(error.Message)
                },
                copyConfig.suspensionId, copyConfig.name,
                organisationId, token
            )
        }
    }

    // tags carry a colour dot; every other multi-select filter (e.g. Sites) shows the plain name
    const renderMultiSelectFilterItemLabel = (label, item) => {
        switch (label) {
            case "Tags":
                return <TagFilterItemLabel tag={ item } />
            default:
                return <p className="m-0">{ item.name }</p>
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "From" || closedLabel === "To" || closedLabel === "Date Range") {
                updatedFilters.From = null
                updatedFilters.To = null
                updatedFilters["Date Range"] = null
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const renderSiteNamePile = (suspensionSites) => {
        if (arrayUtilities.isNullOrEmpty(suspensionSites)) return null
        const MAX_SHOWN = 3
        const shown = suspensionSites.slice(0, MAX_SHOWN).map(s => s.siteName)
        const overflow = suspensionSites.length - MAX_SHOWN
        return overflow > 0
            ? <span>{ shown.join(', ') }, and <strong>{ overflow } more</strong></span>
            : <span>{ shown.join(', ') }</span>
    }

    const handleDelete = () => {
        Api.deleteSuspension(
            data => {
                setDeleteId(null)
                if (data.isError) {
                    setErrorMessage(data.errorMessage)
                } else {
                    getSuspensions(search, pageNumber, selectedSites, filterStart, filterEnd, selectedTags)
                }
            },
            error => {
                setDeleteId(null)
                console.log(error)
                setErrorMessage(error.message)
            },
            deleteId,
            organisationId, token
        )
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card mb-3">
                        <div className="card-header">
                            <div className="row d-flex align-items-center justify-content-between">
                                <div className="col-sm-auto d-flex align-items-center pr-0">
                                    <h5 className="mb-0">Enforcement Suspensions</h5>
                                </div>
                                <div className="align-items-center d-flex">
                                    <div className='mr-2'>
                                        <SearchBarWithDebounce
                                            onSearchQueryChange={query => {
                                                setSearch(query)
                                                setPageNumber(0)
                                                getSuspensions(query, 0, selectedSites, filterStart, filterEnd, selectedTags)
                                            }}
                                        />
                                    </div>
                                    <div className='mr-2'>
                                        <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                    </div>
                                    <div className='d-flex align-items-center'>
                                        { roleUtilities.isOrganisationAdminOrManager(user, organisationId) &&
                                            <>
                                                <Link className="btn btn-success btn-sm ml-1 nowrap" to={ `/organisation/${ organisationId }/enforcement-suspension` }><i className="fas fa-plus mr-1"></i>Add</Link>
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        {
                            showFilterPills && appliedFilters &&
                            <div className="px-3 py-2 border-top d-flex align-items-center flex-wrap">
                                {
                                    Object.entries(appliedFilters).map(
                                        ([label, value]) => <AppliedFilterPill
                                            key={ label }
                                            label={ label }
                                            value={ value }
                                            renderItemLabel={ renderMultiSelectFilterItemLabel }
                                            onTextClick={ () => setShowFiltersModal(true) }
                                            onCloseClick={ handleFilterPillClose }
                                            onRemoveItem={ handleFilterPillItemRemove }
                                        />
                                    )
                                }
                                {appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={() => clearFilters()}>Clear all filters</button>}
                            </div>
                        }
                        <div className="card-body bg-light border-top p-0">
                            <div className="card-body bg-white border-top p-0">
                                <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                    <thead className="bg-200 text-900">
                                        <tr>
                                            <th>Name</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th>Sites</th>
                                            <th className="text-center">Enabled</th>
                                            {
                                                roleUtilities.isOrganisationAdminOrManager(user, organisationId) &&
                                                <th>&nbsp;</th>
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            isLoading ? <tr><td colSpan={ 5 }><Loader /></td></tr>
                                                : suspensions && !arrayUtilities.isNullOrEmpty(suspensions.items)
                                                    ? suspensions.items.map(
                                                        (item, index) => <tr className={isDateTimeElapsed(item.endDate) && "text-muted"} key={ index }>
                                                            <td>
                                                                <Link to={ `/organisation/${ organisationId }/enforcement-suspension/${ item.id }` }><h6 className={`mb-0 ${isDateTimeElapsed(item.endDate) && "text-muted"}`}>
                                                                    { item.name }
                                                                </h6></Link>
                                                            </td>
                                                            <td>{ toDateTimeString(item.startDate) }</td>
                                                            <td>{ toDateTimeString(item.endDate)}</td>
                                                            <td>
                                                                { renderSiteNamePile(item.suspensionSites) }
                                                                {
                                                                    arrayUtilities.isNullOrEmpty(item.suspensionSites) && <HoverTooltip trigger={ <i className="fas fa-exclamation-triangle text-warning fs-1" /> } placement="right" wrapperStyle={ { marginLeft: '8px' } }>This suspension is not associated with any sites</HoverTooltip>
                                                                }
                                                            </td>
                                                            <td className="text-center">
                                                                { item.isEnabled && <i className="fas fa-check text-success"></i> }{ !item.isEnabled && <i className="fas fa-times text-danger"></i> }
                                                            </td>
                                                            {
                                                            roleUtilities.isOrganisationAdminOrManager(user, organisationId) && <td className="text-right">
                                                                <button type="button" onClick={ () => {
                                                                    setCopyConfig({
                                                                        name: `${ item.name } (copy)`,
                                                                        showModal: true,
                                                                        suspensionId: item.id
                                                                    })
                                                                } } className="btn btn-none p-0 m-0">
                                                                    <i className="far fa-copy"></i>
                                                                </button>
                                                                <button type="button" onClick={ () => setDeleteId(item.id) } className="btn btn-none p-0 m-0 ml-2">
                                                                    <i className="fas fa-trash-alt"></i>
                                                                </button>
                                                            </td>}
                                                        </tr>)
                                                    : <tr>
                                                        <td colSpan={ 6 }><EmptyState message="No Data available for the selected filters" /></td>
                                                    </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {
                            !isLoading && suspensions &&
                            <div className="card-footer border-top ">
                                <div className="d-flex justify-content-center row align-items-center">
                                    <div className="col-auto align-items-center">
                                        <Pagination totalResults={ suspensions.totalCount } currentPage={ pageNumber } changePageNumber={ changePageNumber } />
                                    </div>
                                </div>
                            </div>
                        }

                        {
                            copyConfig?.showModal &&
                            <Modal centered show onHide={ () => setCopyConfig(null) }>
                                <Modal.Header>
                                    <Modal.Title>Copy suspension</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <div className="row">
                                        <div className="col-md-3 col-12 ">
                                            <p className="font-weight-semi-bold  mt-1 mb-1">Name<span className="req"></span></p>
                                        </div>
                                        <div className="col-md-9 col-12">
                                            <div className="form-group mb-1">
                                                <input disabled={ isCopying } required className="form-control mb-0" type="text" autoFocus={ true } value={ copyConfig.name } onChange={ (e) => {
                                                    setCopyConfig(prevState => ({
                                                        ...prevState,
                                                        name: e.target.value
                                                    }));
                                                    formUtilities.requiredField(e.target.parentNode, e.target.value)
                                                } } />
                                                <small className='invalid-feedback text-danger'>Please enter a name</small>
                                            </div>
                                        </div>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer>
                                    <button
                                        className="btn btn-sm btn-secondary mt-1"
                                        onClick={ () => setCopyConfig(null) }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        disabled={ stringHelpers.isNullOrEmpty(copyConfig?.name) || isCopying }
                                        className="btn btn-sm btn-primary mt-1"
                                        onClick={ () => handleCopy() }
                                    >
                                        Confirm
                                    </button>
                                </Modal.Footer>
                            </Modal>
                        }
                        {
                            deleteId && <Modal show onHide={ () => setDeleteId(null) } centered>
                                <Modal.Header>
                                    <Modal.Title className="d-flex gap-2 align-items-center text-danger">Delete Suspension</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <p className="font-weight-semi-bold mt-1 mb-2">Are you sure?</p>
                                </Modal.Body>
                                <Modal.Footer>
                                    <button type="button" className="btn btn-falcon-default btn-sm ml-2" onClick={ () => setDeleteId(null) }>Cancel</button>
                                    <button type="button" className="btn btn-danger btn-sm ml-2" onClick={ () => handleDelete() }>Delete</button>
                                </Modal.Footer>
                            </Modal>
                        }
                        {
                            !stringHelpers.isNullOrEmpty(errorMessage) &&
                            <ErrorModal message={ errorMessage } onHide={ () => setErrorMessage(null) } />
                        }
                        {
                            showFiltersModal &&
                            <EnforcementSuspensionsFiltersModal
                                showModal={ showFiltersModal }
                                handleClose={ () => setShowFiltersModal(false) }
                                handleConfirm={ (draftFilters) => {
                                    setFilterStart(draftFilters.filterStart)
                                    setFilterEnd(draftFilters.filterEnd)
                                    setSelectedSites(draftFilters.selectedSites)
                                    setSelectedTags(draftFilters.selectedTags)
                                    setAppliedFilters(draftFilters.appliedFilters)
                                    setShowFilterPills(true)
                                    setShowFiltersModal(false)
                                    setPageNumber(0)
                                    getSuspensions(search, 0, draftFilters.selectedSites, draftFilters.filterStart, draftFilters.filterEnd, draftFilters.selectedTags)
                                } }
                                initialFilterStart={ filterStart }
                                initialFilterEnd={ filterEnd }
                                initialSelectedSites={ selectedSites }
                                initialSelectedTags={ selectedTags }
                                initialAppliedFilters={ appliedFilters }
                                sites={ sitesByClient }
                                tags={ tags || [] }
                                key="enforcement-suspensions-filters-modal"
                            />
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}